import type { LearningProcessApi } from '@mathflat/domain/@entities/StudentLearningProcess/api'
import MathflatPlayer from '@mathflat/videoplayer'
import { observer } from 'mobx-react'
import { type FC, useEffect, useRef } from 'react'
import { isIOS } from 'react-device-detect'

import { commonRepo } from '~/@common/services/repo.service'
import { localStorageService } from '~/@common/services/storage.service'

export type RawLecture = LearningProcessApi.개념학습하기.Output['lectures'][number]
export type Lecture = RawLecture['lecture'] & {
  studentLectureCreateDatatime?: string | null
}

interface Props {
  selectedLecture?: Lecture
  onProgressSubmit: () => Promise<void>
  onAssignmentComplete?: () => Promise<void>
}

const LectureVideoPlayer: FC<Props> = ({
  selectedLecture,
  onProgressSubmit,
  onAssignmentComplete,
}) => {
  const progressSubmittedRef = useRef(false)
  const assignmentCompletedRef = useRef(false)

  // 출제된 강의와 일반 강의의 재생 시간을 별도 키로 관리
  const videoDataKey = onAssignmentComplete ? 'assigned-lecture' : 'lecture'

  const handleProgress = async (time: number) => {
    if (!selectedLecture) {
      return
    }

    const isCompleted =
      onAssignmentComplete &&
      selectedLecture.videoLengthSeconds > 0 &&
      time >= selectedLecture.videoLengthSeconds * 0.9

    // 90% 이상 시청 시 재생 위치를 0으로 초기화, 미만이면 현재 시간 저장
    localStorageService.setVideoData(videoDataKey, selectedLecture.id, {
      current: isCompleted ? 0 : Math.round(time),
    })

    if (!progressSubmittedRef.current && commonRepo.studentId && time >= 30) {
      progressSubmittedRef.current = true
      await onProgressSubmit()
    }
    if (isCompleted && !assignmentCompletedRef.current) {
      assignmentCompletedRef.current = true
      await onAssignmentComplete()
    }
  }

  useEffect(() => {
    progressSubmittedRef.current = false
    assignmentCompletedRef.current = false
  }, [onAssignmentComplete, selectedLecture])

  return (
    <>
      {selectedLecture && (
        <MathflatPlayer
          defaultPlayingTime={
            !isIOS
              ? localStorageService.videoData[videoDataKey].get(selectedLecture.id)?.current
              : undefined
          }
          videoUrl={selectedLecture.videoUrl}
          subtitleUrl={selectedLecture.subtitleUrl ?? undefined}
          ThumbnailElement={
            <div style={{ width: '100%', height: '100%' }}>
              <img src={selectedLecture.thumbnailUrl} style={{ width: '100%', height: '100%' }} />
            </div>
          }
          onEndedCallback={() => {
            localStorageService.removeVideoData(videoDataKey, selectedLecture.id)
          }}
          onProgressCallback={handleProgress}
          isAutoHideControlLayer={true}
        />
      )}
    </>
  )
}

export default observer(LectureVideoPlayer)
